import { Clear } from '@procore/core-icons/dist'
import { useId } from '@react-aria/utils'
import classNames from 'classnames'
import { isNil } from 'ramda'
import React from 'react'
import { Box } from '../Box'
import {
  MenuImperative,
  useMenuImperativeControlNavigation,
} from '../MenuImperative/MenuImperative'
import type { MenuRef, Selection } from '../MenuImperative/MenuImperative.types'
import { Notation } from '../Notation'
import {
  OverlayTrigger,
  useOverlayTriggerContext,
} from '../OverlayTrigger/OverlayTrigger'
import { Spinner } from '../Spinner'
import { Tooltip } from '../Tooltip'
import { useI18nContext } from '../_hooks/I18n'
import { OverflowObserver } from '../_hooks/OverflowObserver'
import { addSubcomponents } from '../_utils/addSubcomponents'
import {
  StyledSelectArrow,
  StyledSelectArrowContainer,
  StyledSelectButton,
  StyledSelectButtonLabel,
  StyledSelectButtonWrapper,
  StyledSelectClearIcon,
  StyledSelectMenu,
  StyledSelectSpinner,
} from './Select.styles'
import type {
  SelectButtonProps,
  SelectMenuProps,
  SelectOptGroupProps,
  SelectOptionProps,
  SelectProps,
  TriggerRole,
} from './Select.types'

function noop() {}
function isFunction(obj: any) {
  return typeof obj === 'function'
}
function isOpen({ open = false }) {
  return open
}

export const SelectButtonFocusContext = React.createContext(false)
export const useSelectButtonFocused = () =>
  React.useContext(SelectButtonFocusContext)

export const OptGroup = React.forwardRef<HTMLDivElement, SelectOptGroupProps>(
  (props, ref) => <MenuImperative.Group {...props} ref={ref} />
)

export const Option = React.forwardRef<HTMLDivElement, SelectOptionProps>(
  ({ value, ...props }, ref) => (
    <MenuImperative.Item ref={ref} {...props} item={value} />
  )
)

export const Button = React.forwardRef<HTMLDivElement, SelectButtonProps>(
  function Button(
    {
      block = false,
      children,
      className,
      clearRef,
      disabled = false,
      error = false,
      focus = false,
      label,
      loading = false,
      onClear,
      open = false,
      placeholder,
      qa,
      tabIndex = 0,
      isMenuOpened = false,
      isListboxOnly = false,
      menuId,
      'aria-expanded': ariaExpandedProp,
      style,
      ...props
    },
    ref
  ) {
    const i18n = useI18nContext()
    const hasClearIcon = Boolean(onClear)
    const showClearIcon = !disabled && !loading && Boolean(label)
    const labelId = useId()
    const triggerRole: TriggerRole = isListboxOnly ? 'combobox' : 'button'

    const content = children || label || placeholder
    const ariaExpanded = disabled
      ? undefined
      : isListboxOnly
      ? ariaExpandedProp
      : open

    const [isFocused, setIsFocused] = React.useState(false)

    const {
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      'aria-labelledby': ariaLabelledByProp,
      ...restProps
    } = props as typeof props & {
      onFocus?: React.FocusEventHandler<HTMLDivElement>
      onBlur?: React.FocusEventHandler<HTMLDivElement>
    }

    const ariaLabelledBy = isListboxOnly
      ? ariaLabelledByProp || labelId || undefined
      : [ariaLabelledByProp, labelId].filter(Boolean).join(' ') || undefined

    const handleFocus = React.useCallback(
      (event: React.FocusEvent<HTMLDivElement>) => {
        setIsFocused(true)
        onFocusProp?.(event)
      },
      [onFocusProp]
    )

    const handleBlur = React.useCallback(
      (event: React.FocusEvent<HTMLDivElement>) => {
        setIsFocused(false)
        onBlurProp?.(event)
      },
      [onBlurProp]
    )

    const button = (
      <StyledSelectButton
        ref={ref}
        role={triggerRole}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : tabIndex}
        aria-expanded={ariaExpanded}
        aria-labelledby={ariaLabelledBy}
        $block={true} // Fill wrapper container
        $error={error}
        $disabled={disabled}
        $hasClearIcon={hasClearIcon}
        $showClearIcon={showClearIcon}
        $loading={loading}
        $open={open}
        $placeholder={!label}
        {...restProps}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <SelectButtonFocusContext.Provider value={isFocused}>
          <OverflowObserver>
            {({ isOverflowingX, ref: overflowRef }) => {
              const showTooltip = !disabled && isOverflowingX && !isMenuOpened
              const trigger = (
                <StyledSelectButtonLabel
                  id={labelId}
                  data-qa={qa?.label}
                  $hoverable={showTooltip}
                  ref={overflowRef}
                >
                  {content}
                </StyledSelectButtonLabel>
              )

              return showTooltip ? (
                <Tooltip
                  key={isFocused ? 'focused' : 'hover-only'}
                  trigger={isFocused ? 'always' : 'hover'}
                  overlay={content}
                >
                  {trigger}
                </Tooltip>
              ) : (
                trigger
              )
            }}
          </OverflowObserver>
        </SelectButtonFocusContext.Provider>
        {loading ? (
          <StyledSelectSpinner>
            <Spinner color="blue50" size="xs" />
          </StyledSelectSpinner>
        ) : (
          <StyledSelectArrowContainer>
            <StyledSelectArrow />
          </StyledSelectArrowContainer>
        )}
      </StyledSelectButton>
    )

    return (
      <StyledSelectButtonWrapper
        $block={block}
        className={classNames(className, {
          focus: isFunction(focus) ? (focus as Function)({ open }) : focus,
        })}
        style={style}
      >
        {button}
        {onClear ? (
          <StyledSelectClearIcon
            aria-label={i18n.t('core.select.clear')}
            ref={clearRef}
            data-qa={qa?.clear || 'core-select-clear'}
            disabled={disabled}
            size="sm"
            variant="tertiary"
            icon={<Clear />}
            onClick={disabled ? undefined : onClear}
            onMouseDown={(e: React.MouseEvent) => e.preventDefault()} // prevents an element from getting the focus
            tabIndex={showClearIcon ? 0 : -1}
          />
        ) : null}
      </StyledSelectButtonWrapper>
    )
  }
)

const SelectMenu = React.forwardRef<HTMLDivElement, SelectMenuProps>(
  function SelectMenu(
    {
      onSearch: onSearch_,
      header,
      footer,
      emptyMessage,
      optionsRef,
      children,
      i18nScope,
      onSelect: onSelect_ = noop,
      menuRef,
      menuId,
      ...props
    },
    ref
  ) {
    const ctx = useOverlayTriggerContext()

    const [searchValue, setSearchValue] = React.useState('')
    const { menuProps, menuNavigationTriggerProps } =
      useMenuImperativeControlNavigation(menuRef, Boolean(onSearch_))

    React.useEffect(() => {
      menuRef.current?.highlightFirst()
    }, [menuRef, searchValue])

    React.useEffect(() => {
      menuRef.current?.highlightSuggested()
      menuRef.current?.highlightSelected()
    }, [menuRef])

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      props.onKeyDown?.(e)
      // This is disconnected from the OverlayTrigger closing on Escape.
      // Closing a dropdown with Escape will not close a Modal, because stopP.
      // It is called only once because the menu component is unmounted.
      // The next Escape will propagate.
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation()
      }
    }

    function onSelect(selection: Selection) {
      onSelect_(selection)

      ctx.hide(selection.event)
    }

    function onSearch(event: React.ChangeEvent<HTMLInputElement>) {
      setSearchValue(event.target.value)

      onSearch_?.(event)
    }

    return (
      <StyledSelectMenu ref={ref} shadowStrength={2}>
        <MenuImperative
          {...props}
          {...menuProps}
          ref={menuRef}
          role="listbox"
          onKeyDown={onKeyDown}
          onSelect={onSelect}
          id={menuId}
          circular
        >
          {onSearch_ && (
            <MenuImperative.Search
              autoComplete="false"
              i18nScope={i18nScope}
              onChange={onSearch}
              {...menuNavigationTriggerProps}
            />
          )}
          {header && <MenuImperative.Header>{header}</MenuImperative.Header>}
          {React.Children.count(children) ? (
            <MenuImperative.Options ref={optionsRef}>
              {children}
            </MenuImperative.Options>
          ) : (
            <Box padding="md lg">
              <Notation variant="pagination">{emptyMessage}</Notation>
            </Box>
          )}
          {footer && <MenuImperative.Footer>{footer}</MenuImperative.Footer>}
        </MenuImperative>
      </StyledSelectMenu>
    )
  }
)

const Select_ = React.forwardRef<HTMLDivElement, SelectProps>(function Select(
  {
    afterHide: afterHide_ = noop,
    afterShow: afterShow_ = noop,
    beforeHide = () => true,
    beforeShow = () => true,
    block = false,
    children,
    className,
    container,
    disabled = false,
    emptyMessage,
    error = false,
    header,
    footer,
    hideDelay = 100,
    i18nScope = 'core.select',
    label = '',
    loading = false,
    onClear: onClear_,
    onKeyDown = noop,
    onScrollBottom,
    onSearch,
    onSelect = noop,
    optionsRef,
    placeholder,
    placement = 'bottom-left',
    restoreFocusOnHide = 'core-react',
    showDelay = 0,
    tabIndex = 0,
    qa,
    ...props
  },
  forwardRef
) {
  const i18n = useI18nContext()
  const menuRef = React.useRef<MenuRef>(null)
  const overlayTriggerRef = React.useRef<HTMLDivElement>(null)
  const ref =
    (forwardRef as React.RefObject<HTMLDivElement>) || overlayTriggerRef
  const [isMenuOpened, setIsMenuOpened] = React.useState(false)

  const clearRef = React.useRef<HTMLButtonElement>(null)
  const menuId = useId()
  const isListboxOnly = !onSearch && !header && !footer

  const emptyMessageText =
    emptyMessage ?? i18n.t('noResult', { scope: i18nScope })

  function afterHide() {
    afterHide_()
    setIsMenuOpened(false)
    onSearch?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const onClear = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClear_?.(e)
      // IE 11 fix
      // Return focus back to trigger instead of the clear button
      overlayTriggerRef?.current?.focus()
    },
    [onClear_]
  )

  function afterShow() {
    afterShow_()
    setIsMenuOpened(true)
  }

  return (
    <OverlayTrigger
      autoFocus
      afterHide={afterHide}
      afterShow={afterShow}
      beforeHide={(e: Event) => {
        if (e.target === clearRef.current) {
          return false
        }

        return beforeHide(e)
      }}
      beforeShow={beforeShow}
      canFlip={true}
      container={container}
      hideDelay={hideDelay}
      restoreFocusOnHide={restoreFocusOnHide}
      role={isListboxOnly ? 'listbox' : undefined}
      passA11yPropsToOverlay={isListboxOnly}
      overlayId={menuId}
      overlay={
        <SelectMenu
          onSearch={onSearch}
          header={header}
          footer={footer}
          emptyMessage={emptyMessageText}
          optionsRef={optionsRef}
          i18nScope={i18nScope}
          onSelect={onSelect}
          onScrollBottom={onScrollBottom}
          menuRef={menuRef}
          menuId={menuId}
        >
          {children}
        </SelectMenu>
      }
      placement={placement}
      ref={ref}
      showDelay={showDelay}
      trigger={disabled ? 'none' : 'click'}
    >
      <Button
        {...props}
        block={block}
        className={className}
        clearRef={clearRef}
        disabled={disabled}
        error={error}
        focus={onSearch ? false : isOpen}
        label={label}
        loading={loading}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        onClear={isNil(onClear_) ? onClear_ : onClear}
        isMenuOpened={isMenuOpened}
        tabIndex={tabIndex}
        isListboxOnly={isListboxOnly}
        menuId={menuId}
        qa={qa}
      />
    </OverlayTrigger>
  )
})

Select_.displayName = 'Select'

Button.displayName = 'Select.Button'

Option.displayName = 'Select.Option'

OptGroup.displayName = 'Select.OptGroup'

/**

 We use single selects to allow our users to choose a single option from a list,
 presented in a dropdown. We typically see these selects on forms.

 If you want users to select multiple options, use a multi select, group select,
 and tiered select if you want users to select from a tiered set of options,
 use a tiered select.

 @since 10.19.0

 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-select--demo)

 @see [Design Guidelines](https://design.procore.com/select)

 */
export const Select = addSubcomponents(
  {
    Button,
    Option,
    OptGroup,
  },
  Select_
)
